'use client'

import React, { useState, useMemo, useCallback } from 'react'
import styles from './TerminalTabs.module.css'
import { useBinanceOpenOrders, useBinanceQueryInvalidation } from './hooks/useBinanceData'
import { usePathname, useSearchParams } from 'next/navigation'
import { ConnectorManager } from '../ConnectorManager'
import { getSecureItem } from '../utils/secureStorage'

/**
 * @uiComponent
 * @name OpenOrders
 * @description The OpenOrders component displays pending cryptocurrency orders on the Binance platform.
 * It shows detailed information about each open order including symbol, price, quantity, status,
 * and order types. Features filtering by symbol, expandable order groups for stop-loss/take-profit
 * combinations, and URL-based symbol filtering for focused trading views.
 * @location Open Orders tab in the trading terminal
 * @prop none - No external props, manages internal state and connector integration
 */

/**
 * @uiWorkflow
 * @name View and Manage Open Crypto Orders on Binance
 * @description How to monitor and understand your pending cryptocurrency orders
 * @platform binance
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Open Orders" tab [component: OpenOrders] [action: click]
 * @step 3 Review all pending orders in the table [component: OpenOrders] [action: observe]
 * @step 4 Use the symbol dropdown to filter orders by specific crypto pairs [component: OpenOrders] [action: select]
 * @step 5 Check the "Hide other pairs" option to focus on current symbol [component: OpenOrders] [action: check] [notes: Only available when viewing a specific crypto pair page]
 * @step 6 Click the arrow button to expand order groups with take-profit/stop-loss [component: OpenOrders] [action: click]
 * @step 7 Review order details including prices, quantities, and status [component: OpenOrders] [action: observe]
 * @step 8 Monitor order execution status and time stamps [component: OpenOrders] [action: observe]
 * @step 9 Cancel orders that are no longer needed [component: OpenOrders] [action: click]
 */

/**
 * @uiWorkflow
 * @name Analyze Stop-Loss and Take-Profit Orders on Binance
 * @description How to review and understand your risk management order configurations
 * @platform binance
 * @step 1 Open the "Open Orders" tab in the trading terminal [component: OpenOrders] [action: click]
 * @step 2 Look for grouped orders (shown with expandable arrows) [component: OpenOrders] [action: observe]
 * @step 3 Click the arrow button to expand stop-loss/take-profit order groups [component: OpenOrders] [action: click]
 * @step 4 Review the main limit order details [component: OpenOrders] [action: observe]
 * @step 5 Check the take-profit order configuration and trigger price [component: OpenOrders] [action: observe]
 * @step 6 Examine the stop-loss order settings and stop price [component: OpenOrders] [action: observe]
 * @step 7 Verify order types (LIMIT, LIMIT_MAKER, STOP_LOSS, etc.) [component: OpenOrders] [action: observe]
 * @step 8 Monitor execution status and filled quantities [component: OpenOrders] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Filter Orders by Cryptocurrency Pair on Binance
 * @description How to focus on orders for specific trading pairs
 * @platform binance
 * @step 1 Access the Open Orders tab [component: OpenOrders] [action: click]
 * @step 2 Locate the symbol dropdown at the top of the orders list [component: OpenOrders] [action: observe]
 * @step 3 Click the dropdown to see all available crypto pairs [component: OpenOrders] [action: click]
 * @step 4 Select a specific trading pair to filter orders [component: OpenOrders] [action: select]
 * @step 5 View only orders related to the selected pair [component: OpenOrders] [action: observe]
 * @step 6 Use "All" option to see all orders again [component: OpenOrders] [action: select]
 * @step 7 When on a crypto pair page, use "Hide other pairs" for focused view [component: OpenOrders] [action: check]
 * @step 8 Review filtered results for better order management [component: OpenOrders] [action: analyze]
 */

interface OpenOrder {
  symbol: string
  orderId: number
  orderListId: number
  clientOrderId: string
  price: string
  origQty: string
  executedQty: string
  cummulativeQuoteQty: string
  status: string
  timeInForce: string
  type: string
  side: string
  stopPrice: string
  icebergQty: string
  time: number
  updateTime: number
  isWorking: boolean
  origQuoteOrderQty: string
}

// Interface to group orders
interface OrderGroup {
  mainOrder: OpenOrder
  takeProfit?: OpenOrder
  stopLoss?: OpenOrder
  expanded: boolean
}

export default function OpenOrders() {
  const searchParams = useSearchParams()

  // Get symbol from URL path instead of query parameters
  const pathname = usePathname()
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [selectedSymbol, setSelectedSymbol] = useState('ALL')
  const [hideOtherPairs, setHideOtherPairs] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<{
    [key: number]: boolean
  }>({})
  const [cancellingOrders, setCancellingOrders] = useState<Set<string>>(new Set())

  // Use React Query hook instead of useEffect for data fetching
  const { data: allOpenOrders = [], isLoading, error } = useBinanceOpenOrders()
  
  // Get query invalidation functions
  const { invalidateOpenOrders } = useBinanceQueryInvalidation()
  
  // Get connector instance for cancelling orders
  const connectorManager = ConnectorManager.getInstance()
  // Get the enabled connector (binance or ninx)
  // If on /x/ routes, default to ninx
  const isOnXRoute = pathname?.startsWith('/x/')
  const enabledConnector = isOnXRoute ? 'ninx' : (getSecureItem('broker_enabled') || 'ninx')
  const connector = enabledConnector === 'binance' ?
    connectorManager.getConnector('binance') :
    enabledConnector === 'ninx' ?
      connectorManager.getConnector('ninx') : null

  // Compute filtered orders and available symbols using useMemo instead of useEffect
  const { openOrders, availableSymbols } = useMemo(() => {
    // Extract unique symbols from orders
    const symbols = ['ALL', ...new Set(allOpenOrders.map(order => order.symbol))]
    
    // Filter orders based on selected symbol and hideOtherPairs
    let filteredOrders = allOpenOrders

    // If hideOtherPairs is checked and we have a URL symbol, only show that symbol
    if (hideOtherPairs && urlSymbol) {
      filteredOrders = allOpenOrders.filter(order => order.symbol === urlSymbol)
    }
    // Otherwise filter by the dropdown selection
    else if (selectedSymbol !== 'ALL') {
      filteredOrders = allOpenOrders.filter(order => order.symbol === selectedSymbol)
    }

    return {
      openOrders: filteredOrders,
      availableSymbols: symbols
    }
  }, [allOpenOrders, selectedSymbol, hideOtherPairs, urlSymbol])

  const handleSymbolChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSymbol(e.target.value)
  }, [])

  const handleHideOtherPairsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setHideOtherPairs(e.target.checked)
  }, [])

  const formatDate = useCallback((timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }, [])

  // Group orders by orderListId using useMemo for performance
  const groupOrdersByOrderListId = useCallback((orders: OpenOrder[]): OrderGroup[] => {
    const orderGroups: { [key: number]: OpenOrder[] } = {}

    // Group orders by orderListId
    orders.forEach(order => {
      if (order.orderListId > 0) {
        if (!orderGroups[order.orderListId]) {
          orderGroups[order.orderListId] = []
        }
        orderGroups[order.orderListId].push(order)
      }
    })

    // Create order groups with main order, TP, and SL
    const result: OrderGroup[] = []

    // First add all orders that are not part of a group
    orders.forEach(order => {
      if (order.orderListId === 0 || order.orderListId === -1) {
        result.push({
          mainOrder: order,
          expanded: !!expandedGroups[order.orderId]
        })
      }
    })

    // Then add grouped orders
    Object.entries(orderGroups).forEach(([orderListId, groupOrders]) => {
      if (groupOrders.length > 1) {
        // Find the main order (usually LIMIT), TP (usually LIMIT_MAKER), and SL (usually STOP_LOSS or STOP_LOSS_LIMIT)
        const mainOrder =
          groupOrders.find(o => o.type === 'LIMIT') || groupOrders[0]
        const takeProfit = groupOrders.find(o => o.type === 'LIMIT_MAKER')
        const stopLoss = groupOrders.find(
          o =>
            o.type === 'STOP_LOSS' ||
            o.type === 'STOP_LOSS_LIMIT' ||
            o.type === 'STOP_MARKET'
        )

        result.push({
          mainOrder,
          takeProfit,
          stopLoss,
          expanded: !!expandedGroups[parseInt(orderListId)]
        })
      } else if (groupOrders.length === 1) {
        // If there's only one order in the group, add it as a single order
        result.push({
          mainOrder: groupOrders[0],
          expanded: !!expandedGroups[groupOrders[0].orderId]
        })
      }
    })

    return result
  }, [expandedGroups])

  // Toggle expanded state
  const toggleExpanded = useCallback((id: number) => {
    setExpandedGroups(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }, [])

  // Handle order cancellation
  const handleCancelOrder = useCallback(async (orderId: string, symbol?: string) => {
    if (!connector) {
      console.error('No connector available for cancelling orders')
      return
    }

    // Add to cancelling set to show loading state
    setCancellingOrders(prev => new Set(prev).add(orderId))

    try {
      const success = await connector.cancelOrder(orderId, symbol)
      
      if (success) {
        console.log(`Order ${orderId} cancelled successfully`)
        // Invalidate and refetch open orders to update the UI
        invalidateOpenOrders()
      } else {
        console.error(`Failed to cancel order ${orderId}`)
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error cancelling order:', error)
      // You could add error handling/notification here
    } finally {
      // Remove from cancelling set
      setCancellingOrders(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
    }
  }, [connector, invalidateOpenOrders])

  // Memoize grouped orders for performance
  const groupedOrders = useMemo(() => {
    return groupOrdersByOrderListId(openOrders)
  }, [openOrders, groupOrdersByOrderListId])

  if (isLoading) {
    return <div className={styles.loadingState}>Loading open orders...</div>
  }
  
  if (error) {
    return <div className={styles.errorState}>{error instanceof Error ? error.message : String(error)}</div>
  }

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={selectedSymbol}
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
            disabled={hideOtherPairs && urlSymbol ? true : false}
          >
            {availableSymbols.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol === 'ALL' ? 'All Symbols' : symbol}
              </option>
            ))}
          </select>
        </div>

        {urlSymbol && (
          <div className={styles.filterOption}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideOtherPairs}
                onChange={handleHideOtherPairsChange}
                className={styles.checkbox}
              />
              Hide other pairs
            </label>
          </div>
        )}
      </div>

      {openOrders.length === 0 ? (
        <div className={styles.emptyState}>
          {hideOtherPairs && urlSymbol
            ? `No open orders found for ${urlSymbol}`
            : selectedSymbol === 'ALL'
              ? 'No open orders found'
              : `No open orders found for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Stop Price</th>
                <th>Amount</th>
                <th>Filled</th>
                <th>Total</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {groupedOrders.map(group => {
                const { mainOrder, takeProfit, stopLoss, expanded } = group
                const hasRelatedOrders = takeProfit || stopLoss

                const price = parseFloat(mainOrder.price)
                const amount = parseFloat(mainOrder.origQty)
                const filled = parseFloat(mainOrder.executedQty)
                const total = price * amount
                const stopPrice = parseFloat(mainOrder.stopPrice)

                return (
                  <React.Fragment key={mainOrder.orderId}>
                    <tr className={hasRelatedOrders ? styles.groupMainRow : ''}>
                      <td>
                        {hasRelatedOrders && (
                          <button
                            className={styles.expandButton}
                            onClick={() =>
                              toggleExpanded(mainOrder.orderListId)
                            }
                          >
                            {expanded ? '▼' : '►'}
                          </button>
                        )}
                      </td>
                      <td>{formatDate(mainOrder.time)}</td>
                      <td>{mainOrder.symbol}</td>
                      <td>{mainOrder.type}</td>
                      <td
                        className={
                          mainOrder.side === 'BUY'
                            ? styles.buyOrder
                            : styles.sellOrder
                        }
                      >
                        {mainOrder.side}
                      </td>
                      <td>${price.toFixed(price < 10.0 ? 8 : 2)}</td>
                      <td>
                        {stopPrice > 0
                          ? `$${stopPrice.toFixed(stopPrice < 10.0 ? 8 : 2)}`
                          : '-'}
                      </td>
                      <td>{amount.toFixed(8)}</td>
                      <td>{filled.toFixed(8)}</td>
                      <td>${total.toFixed(2)}</td>
                      <td>{mainOrder.status}</td>
                      <td>
                        {mainOrder.status === 'NEW' || mainOrder.status === 'PARTIALLY_FILLED' ? (
                          <button
                            className={styles.cancelButton}
                            onClick={() => handleCancelOrder(mainOrder.orderId.toString(), mainOrder.symbol)}
                            disabled={cancellingOrders.has(mainOrder.orderId.toString())}
                          >
                            {cancellingOrders.has(mainOrder.orderId.toString()) ? 'Cancelling...' : 'Cancel'}
                          </button>
                        ) : (
                          <span className={styles.disabledAction}>-</span>
                        )}
                      </td>
                    </tr>

                    {hasRelatedOrders && expanded && (
                      <>
                        <tr className={styles.subRow}>
                          <td></td>
                          <td colSpan={11} className={styles.subRowInfo}>
                            <div className={styles.orderDescription}>
                              <strong>A (Limit Order):</strong> If Order A is
                              filled fully, orders B and C will be placed
                            </div>
                          </td>
                        </tr>

                        {takeProfit && (
                          <tr className={styles.subRow}>
                            <td></td>
                            <td colSpan={11} className={styles.subRowInfo}>
                              <div className={styles.orderDescription}>
                                <strong>B (Take Profit):</strong> If order B is
                                triggered, order C will be cancelled
                              </div>
                              <div className={styles.orderDetails}>
                                Type: {takeProfit.type}, Side:{' '}
                                <span
                                  className={
                                    takeProfit.side === 'BUY'
                                      ? styles.buyOrder
                                      : styles.sellOrder
                                  }
                                >
                                  {takeProfit.side}
                                </span>
                                , Price: $
                                {parseFloat(takeProfit.price).toFixed(2)}
                              </div>
                            </td>
                          </tr>
                        )}

                        {stopLoss && (
                          <tr className={styles.subRow}>
                            <td></td>
                            <td colSpan={11} className={styles.subRowInfo}>
                              <div className={styles.orderDescription}>
                                <strong>C (Stop Loss):</strong> If order C is
                                triggered, order B will be cancelled
                              </div>
                              <div className={styles.orderDetails}>
                                Type: {stopLoss.type}, Side:{' '}
                                <span
                                  className={
                                    stopLoss.side === 'BUY'
                                      ? styles.buyOrder
                                      : styles.sellOrder
                                  }
                                >
                                  {stopLoss.side}
                                </span>
                                , Stop Price: $
                                {parseFloat(stopLoss.stopPrice).toFixed(2)}
                                {stopLoss.type === 'STOP_LOSS_LIMIT' &&
                                  parseFloat(stopLoss.price) > 0 && (
                                    <>
                                      , Limit Price: $
                                      {parseFloat(stopLoss.price).toFixed(2)}
                                    </>
                                  )}
                                {stopLoss.type === 'STOP_LOSS' &&
                                  parseFloat(stopLoss.price) === 0 && (
                                    <> (Market Order)</>
                                  )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
